import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { loadGoogleMaps } from "@/lib/googleMaps";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Square, Slash, Ruler, Undo2, Redo2, Trash2, Eye, Layers, MapPin, CheckCircle2, Loader2,
} from "lucide-react";
import SolarReviewCard from "./SolarReviewCard";
import {
  computeMeasurement,
  formatMeasurementLabel,
  serviceColor,
} from "./utils";
import {
  MEASUREMENT_CATEGORIES,
  resolveAutoLink,
  categoryLabel,
} from "@/lib/measurementCategoryMap";
import CategoryIcon from "@/components/CategoryIcon";

/**
 * GoogleWorkspace — Iter D.1 flag-gated Google Maps measurement canvas.
 *
 * Flow (D.1):
 *   1. User draws a shape and clicks Finish.
 *   2. Workspace requires a service category — inferred from the active
 *      estimate line when supplied, otherwise the user is prompted.
 *      "Other" requires a custom label.
 *   3. Measurement is persisted (POST /api/measurements) IMMEDIATELY,
 *      tenant-scoped and property-scoped, initially unlinked.
 *   4. The newly saved measurement appears as a card, auto-selected.
 *   5. In estimate context: bottom action button links selected measurements
 *      to compatible lines via POST /api/measurements/{id}/links.
 *   6. Standalone context: no action button, informational text only.
 *
 * Persistence policy: only user-drawn GeoJSON, computed metrics, category,
 * address, one lat/lng point, and provider name are persisted. No imagery.
 */
export default function GoogleWorkspace({
  propertyAddress,
  propertyLat,
  propertyLng,
  serviceCategory,          // default category inferred from active line (optional)
  onSaved,                  // called when a new measurement is persisted
  onClose,
  solarEnabled,
  // Iter D.1 — estimate context (optional)
  estimateId,               // when set, workspace is in estimate context
  estimateLineItems,        // array of line items in original order
  targetLineItemIndex,      // optional preferred index (e.g. per-line entry)
  onLinked,                 // callback({ estimate, measurement, line_item_index })
  initialFocusMeasurementId, // Iter57.2 — measurement to fitBounds+pre-select on open
}) {
  const mapDivRef = useRef(null);
  const streetDivRef = useRef(null);
  const searchInputRef = useRef(null);
  const [googleReady, setGoogleReady] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  const [mapType, setMapType] = useState("satellite");
  const [tool, setTool] = useState("area");
  const [center, setCenter] = useState(
    propertyLat != null && propertyLng != null
      ? { lat: propertyLat, lng: propertyLng }
      : null
  );
  const [geocodePending, setGeocodePending] = useState(
    (propertyLat == null || propertyLng == null) && !!propertyAddress
  );
  const [drawings, setDrawings] = useState([]);
  const [activeDraft, setActiveDraft] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [showStreet, setShowStreet] = useState(false);
  const displayUnits = "imperial";

  // Iter D.1 state
  const [savedMeasurements, setSavedMeasurements] = useState([]); // [{id, ..., link_status}]
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [linking, setLinking] = useState(false);
  const [pendingFinish, setPendingFinish] = useState(null); // {mode, path, computed}
  const [categoryDialog, setCategoryDialog] = useState(null); // { measurementDraft, resolve }
  const [linkPickerDialog, setLinkPickerDialog] = useState(null); // { measurement, candidates }

  const mapRef = useRef(null);
  const shapesRef = useRef({});
  const draftShapeRef = useRef(null);

  const inEstimateContext = !!estimateId;
  const lineItems = estimateLineItems || [];

  // ---- Load Google Maps ----
  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then(() => { if (!cancelled) setGoogleReady(true); })
      .catch((e) => setLoadErr(String(e?.message || e)));
    return () => { cancelled = true; };
  }, []);
  const google = typeof window !== "undefined" ? window.google : null;

  // ---- Fetch existing saved measurements for this property (with link status) ----
  const refreshSavedMeasurements = useCallback(async () => {
    try {
      const url = estimateId
        ? `/measurements?estimate_id=${encodeURIComponent(estimateId)}`
        : `/measurements`;
      const { data } = await api.get(url);
      // Filter to this property when we have coords or an address.
      const filtered = (data || []).filter((m) => {
        if (propertyAddress && m.property_address) {
          return m.property_address === propertyAddress;
        }
        if (propertyLat != null && propertyLng != null &&
            m.property_lat != null && m.property_lng != null) {
          return Math.abs(m.property_lat - propertyLat) < 1e-4 &&
                 Math.abs(m.property_lng - propertyLng) < 1e-4;
        }
        return true;
      });
      setSavedMeasurements(filtered);
    } catch (e) {
      // Non-fatal — user can still draw/save.
    }
  }, [estimateId, propertyAddress, propertyLat, propertyLng]);

  useEffect(() => { refreshSavedMeasurements(); }, [refreshSavedMeasurements]);

  // Iter57.2 — reuse focus: when the caller passed initialFocusMeasurementId
  // (from the pre-workspace ReuseMeasurementDialog), pan/zoom the map to
  // that measurement's polygon and pre-select its card so the user can
  // visually verify before pressing "Add to Estimate".
  useEffect(() => {
    if (!initialFocusMeasurementId || !savedMeasurements.length) return;
    const m = savedMeasurements.find((x) => x.id === initialFocusMeasurementId);
    if (!m) return;
    // Pre-select the card.
    setSelectedIds((s) => { const n = new Set(s); n.add(m.id); return n; });
    // Fit map bounds to the polygon/linestring when the map is ready.
    if (mapRef.current && google && m.geometry) {
      try {
        const bounds = new google.maps.LatLngBounds();
        const coords =
          m.geometry.type === "Polygon" ? (m.geometry.coordinates?.[0] || [])
          : m.geometry.type === "LineString" ? (m.geometry.coordinates || [])
          : [];
        for (const [lng, lat] of coords) bounds.extend(new google.maps.LatLng(lat, lng));
        if (!bounds.isEmpty()) mapRef.current.fitBounds(bounds, 64);
      } catch { /* non-fatal */ }
    }
  }, [initialFocusMeasurementId, savedMeasurements, google]);

  // ---- Init map ----
  useEffect(() => {
    if (!googleReady || !google || !mapDivRef.current || mapRef.current || !center) return;
    const map = new google.maps.Map(mapDivRef.current, {
      center, zoom: 20,
      mapTypeId: mapType === "hybrid" ? google.maps.MapTypeId.HYBRID : google.maps.MapTypeId.SATELLITE,
      tilt: 0, gestureHandling: "greedy", disableDefaultUI: true,
      zoomControl: true, streetViewControl: false, fullscreenControl: false, keyboardShortcuts: false,
    });
    mapRef.current = map;
    map.addListener("click", (e) => _onMapClick(e.latLng));

    if (searchInputRef.current && google.maps.places) {
      const ac = new google.maps.places.Autocomplete(searchInputRef.current, {
        fields: ["formatted_address", "geometry"],
      });
      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        if (!place || !place.geometry || !place.geometry.location) return;
        const loc = place.geometry.location;
        const c = { lat: loc.lat(), lng: loc.lng() };
        map.setCenter(c); map.setZoom(20); setCenter(c);
      });
    }
  }, [googleReady, center]); // eslint-disable-line

  // ---- Geocode fallback for missing coords ----
  useEffect(() => {
    if (!googleReady || !google || !geocodePending || !propertyAddress) return;
    let cancelled = false;
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: propertyAddress }, (results, status) => {
      if (cancelled) return;
      if (status === "OK" && results && results[0]) {
        const loc = results[0].geometry.location;
        setCenter({ lat: loc.lat(), lng: loc.lng() });
      }
      setGeocodePending(false);
    });
    return () => { cancelled = true; };
  }, [googleReady, google, geocodePending, propertyAddress]);

  useEffect(() => {
    if (!mapRef.current || !google) return;
    mapRef.current.setMapTypeId(
      mapType === "hybrid" ? google.maps.MapTypeId.HYBRID : google.maps.MapTypeId.SATELLITE
    );
  }, [mapType, google]);

  // ---- Drawing draft ----
  const _onMapClick = (latLng) => {
    if (!latLng) return;
    setActiveDraft((d) => {
      if (!d) return { mode: tool, path: [{ lat: latLng.lat(), lng: latLng.lng() }] };
      return { ...d, path: [...d.path, { lat: latLng.lat(), lng: latLng.lng() }] };
    });
  };

  useEffect(() => {
    if (!mapRef.current || !google) return;
    if (draftShapeRef.current) { draftShapeRef.current.setMap(null); draftShapeRef.current = null; }
    if (!activeDraft || activeDraft.path.length < 2) return;
    const color = serviceColor(serviceCategory);
    const path = activeDraft.path.map((p) => new google.maps.LatLng(p.lat, p.lng));
    if (activeDraft.mode === "area" || activeDraft.mode === "perimeter" || activeDraft.mode === "roof") {
      draftShapeRef.current = new google.maps.Polygon({
        paths: path, map: mapRef.current, strokeColor: color, strokeOpacity: 0.9,
        strokeWeight: 2, fillColor: color, fillOpacity: 0.2, editable: false, clickable: false,
      });
    } else {
      draftShapeRef.current = new google.maps.Polyline({
        path, map: mapRef.current, strokeColor: color, strokeOpacity: 0.9,
        strokeWeight: 3, editable: false, clickable: false,
      });
    }
  }, [activeDraft, google, serviceCategory]);

  // Materialize finished shapes (only for the current session's drawings)
  useEffect(() => {
    if (!mapRef.current || !google) return;
    for (const id of Object.keys(shapesRef.current)) {
      if (!drawings.find((d) => d.id === id)) {
        shapesRef.current[id].setMap(null);
        delete shapesRef.current[id];
      }
    }
    for (const d of drawings) {
      if (shapesRef.current[d.id]) continue;
      const path = d.path.map((p) => new google.maps.LatLng(p.lat, p.lng));
      const color = serviceColor(d.service_category || serviceCategory);
      const shape = d.mode === "linear"
        ? new google.maps.Polyline({
            path, map: mapRef.current, strokeColor: color, strokeOpacity: 0.95,
            strokeWeight: 3, editable: true, draggable: true,
          })
        : new google.maps.Polygon({
            paths: path, map: mapRef.current, strokeColor: color, strokeOpacity: 0.95,
            strokeWeight: 2, fillColor: color, fillOpacity: 0.25, editable: true, draggable: true,
          });
      shapesRef.current[d.id] = shape;
    }
  }, [drawings, google, serviceCategory]);

  const _pushUndo = () => { setUndoStack((s) => [...s, drawings]); setRedoStack([]); };
  const undo = () => {
    setUndoStack((s) => {
      if (s.length === 0) return s;
      const prev = s[s.length - 1];
      setRedoStack((r) => [...r, drawings]);
      setDrawings(prev);
      return s.slice(0, -1);
    });
  };
  const redo = () => {
    setRedoStack((s) => {
      if (s.length === 0) return s;
      const next = s[s.length - 1];
      setUndoStack((u) => [...u, drawings]);
      setDrawings(next);
      return s.slice(0, -1);
    });
  };
  const clearAll = () => {
    if (drawings.length === 0) return;
    if (!window.confirm("Clear all drawings on the map?")) return;
    _pushUndo();
    setDrawings([]);
    setActiveDraft(null);
  };

  // ---- Finish draft: category required, then auto-save ----
  const finishDraft = () => {
    if (!activeDraft || !google) return;
    const minPoints = activeDraft.mode === "linear" ? 2 : 3;
    if (activeDraft.path.length < minPoints) {
      toast.error(`Need at least ${minPoints} points`);
      return;
    }
    const draft = { ...activeDraft, path: [...activeDraft.path] };

    // Category inference:
    //  - If default category prop is supplied (e.g. from active estimate line) → use it directly.
    //  - Otherwise prompt the user to pick a category (and optionally a custom label for "other").
    if (serviceCategory) {
      _saveDraft(draft, { service_category: serviceCategory, notes: null });
      return;
    }
    setCategoryDialog({ draft });
  };
  const cancelDraft = () => setActiveDraft(null);

  const _saveDraft = async (draft, { service_category, notes }) => {
    setSaving(true);
    try {
      const geometry = draft.mode === "linear"
        ? { type: "LineString", coordinates: draft.path.map((p) => [p.lng, p.lat]) }
        : { type: "Polygon", coordinates: [_ringFromPath(draft.path)] };
      const r = computeMeasurement({ measurementType: draft.mode, geometry, google, displayUnits });
      const payload = {
        measurement_type: draft.mode === "linear" ? "linear" : (draft.mode === "perimeter" ? "perimeter" : "area"),
        provider: "google",
        geometry,
        square_feet: r.square_feet ?? null,
        linear_feet: r.linear_feet ?? null,
        metric_value: r.metric_value ?? null,
        display_units: displayUnits,
        service_category,
        property_address: propertyAddress || null,
        property_lat: propertyLat ?? (center?.lat ?? null),
        property_lng: propertyLng ?? (center?.lng ?? null),
        notes: notes || null,
      };
      const { data } = await api.post("/measurements", payload);
      // Remember the shape locally so the user still sees it on the map.
      const id = `d_${data.id}`;
      _pushUndo();
      setDrawings((xs) => [...xs, { id, mode: draft.mode, path: draft.path, service_category, backend_id: data.id }]);
      setActiveDraft(null);
      // Add to saved list & auto-select
      const enriched = { ...data, link_status: { linked_to_this_estimate: false, linked_line_items_here: [], total_links: 0 } };
      setSavedMeasurements((xs) => [enriched, ...xs.filter((m) => m.id !== data.id)]);
      setSelectedIds((s) => { const n = new Set(s); n.add(data.id); return n; });
      toast.success("Measurement saved");
      if (onSaved) onSaved(enriched);
    } catch (e) {
      toast.error("Failed to save measurement");
    } finally {
      setSaving(false);
    }
  };

  // ---- Selection helpers ----
  const toggleSelect = (id) => {
    setSelectedIds((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const selectedCount = selectedIds.size;

  const selectableMeasurements = useMemo(() => {
    // In estimate context: selectable = saved AND not already linked to this estimate.
    if (!inEstimateContext) return savedMeasurements;
    return savedMeasurements.filter((m) => !m?.link_status?.linked_to_this_estimate);
  }, [savedMeasurements, inEstimateContext]);

  const canLink = inEstimateContext && selectedCount > 0 &&
    Array.from(selectedIds).some((id) => selectableMeasurements.find((m) => m.id === id));

  // ---- Delete measurement (409-aware) ----
  const deleteMeasurement = async (m) => {
    if (!window.confirm(`Delete "${categoryLabel(m.service_category) || "measurement"}"?`)) return;
    try {
      await api.delete(`/measurements/${m.id}`);
      setSavedMeasurements((xs) => xs.filter((x) => x.id !== m.id));
      setSelectedIds((s) => { const n = new Set(s); n.delete(m.id); return n; });
      // Also remove any local draft mirroring this backend id.
      setDrawings((xs) => xs.filter((d) => d.backend_id !== m.id));
      toast.success("Measurement deleted");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.error_kind === "measurement_linked") {
        const ids = detail.linked_estimate_ids || [];
        toast.error(
          <div data-testid="delete-linked-toast">
            <div className="font-medium">Can't delete — measurement is in use</div>
            <div className="text-xs mt-1">
              Linked to {ids.length} estimate{ids.length === 1 ? "" : "s"}. Unlink first or delete the estimate line.
            </div>
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.error("Delete failed");
      }
    }
  };

  // ---- Link selected → estimate ----
  const linkOne = async (measurement, lineItemIndex) => {
    const { data } = await api.post(`/measurements/${measurement.id}/links`, {
      estimate_id: estimateId,
      line_item_index: lineItemIndex,
    });
    // Mark this measurement as linked for the current estimate context.
    setSavedMeasurements((xs) => xs.map((m) =>
      m.id === measurement.id
        ? {
            ...m,
            link_status: {
              linked_to_this_estimate: true,
              linked_line_items_here: [String(lineItemIndex)],
              total_links: (m?.link_status?.total_links || 0) + 1,
            },
          }
        : m
    ));
    setSelectedIds((s) => { const n = new Set(s); n.delete(measurement.id); return n; });
    if (onLinked) onLinked({ estimate: data.estimate, measurement, line_item_index: lineItemIndex });
    return data;
  };

  const performLink = async () => {
    if (!inEstimateContext) return;
    setLinking(true);
    try {
      const toLink = Array.from(selectedIds)
        .map((id) => savedMeasurements.find((m) => m.id === id))
        .filter((m) => m && !m?.link_status?.linked_to_this_estimate);
      if (toLink.length === 0) {
        toast.error("Nothing to add");
        setLinking(false);
        return;
      }
      // If a target line was passed (per-line entry), link everything to it.
      if (typeof targetLineItemIndex === "number") {
        for (const m of toLink) {
          await linkOne(m, targetLineItemIndex);
        }
        toast.success(toLink.length === 1 ? "Added to estimate" : `Added ${toLink.length} to estimate`);
        setLinking(false);
        return;
      }
      // Otherwise resolve auto-link per measurement.
      let firstMulti = null;
      for (const m of toLink) {
        const r = resolveAutoLink({ measurement: m, estimateLineItems: lineItems });
        if (typeof r.line_item_index === "number") {
          await linkOne(m, r.line_item_index);
        } else if (r.candidates && r.candidates.length > 0) {
          firstMulti = { measurement: m, candidates: r.candidates, matched_by: r.matched_by };
          break;
        } else {
          // Zero candidates
          firstMulti = { measurement: m, candidates: [], matched_by: "none" };
          break;
        }
      }
      if (firstMulti) {
        setLinkPickerDialog(firstMulti);
      } else {
        toast.success(toLink.length === 1 ? "Added to estimate" : `Added ${toLink.length} to estimate`);
      }
    } catch (e) {
      toast.error(`Link failed: ${e?.response?.data?.detail || e?.message || ""}`);
    } finally {
      setLinking(false);
    }
  };

  // Street View
  useEffect(() => {
    if (!showStreet || !google || !streetDivRef.current || !mapRef.current) return;
    const c = mapRef.current.getCenter?.() || new google.maps.LatLng(center.lat, center.lng);
    const pano = new google.maps.StreetViewPanorama(streetDivRef.current, {
      position: { lat: c.lat(), lng: c.lng() }, pov: { heading: 0, pitch: 0 }, visible: true,
    });
    mapRef.current.setStreetView(pano);
    return () => pano.setVisible(false);
  }, [showStreet, google]); // eslint-disable-line

  // Solar callback (unchanged path)
  const onSolarConfirm = async (insights) => {
    const areaM2 = insights?.total_roof_area_m2;
    if (!areaM2) { toast.error("Solar insights had no roof area"); return; }
    const c = insights.center || { lat: propertyLat, lng: propertyLng };
    const geometry = { type: "Polygon", coordinates: [_pointBbox(c.lat, c.lng, 5)] };
    setSaving(true);
    try {
      // Iter D.0 — Solar measurement metadata (all optional; backend stores
      // as null when omitted). Panel count derived from summing configuration
      // roof segments when present. Never persist raw imagery.
      const segments = insights.roof_segments || [];
      const solar_metadata = {
        panel_count: insights.max_array_panels_count || null,
        panel_area_sqft: insights.panel_area_m2 ? insights.panel_area_m2 * 10.7639 : null,
        array_area_sqft: insights.max_array_area_m2 ? insights.max_array_area_m2 * 10.7639 : (areaM2 * 10.7639),
        orientation: segments.length ? String(Math.round(segments[0].azimuth_degrees || 0)) : null,
        tilt: segments.length ? (segments[0].pitch_degrees ?? null) : null,
        confidence: insights.imagery_quality || null,
        solar_source: "google_solar_building_insights",
      };
      const payload = {
        measurement_type: "area",
        provider: "solar",
        geometry,
        metric_value: areaM2,
        square_feet: areaM2 * 10.7639,
        display_units: displayUnits,
        service_category: "solar",  // Iter D.0 — first-class Solar category
        property_address: propertyAddress || null,
        property_lat: c.lat, property_lng: c.lng,
        notes: `Google Solar imagery ${insights.imagery_quality || ""} ${
          insights.imagery_date ? `${insights.imagery_date.year}-${insights.imagery_date.month}` : ""
        }`.trim(),
        solar_metadata,
      };
      const { data } = await api.post("/measurements", payload);
      const enriched = { ...data, link_status: { linked_to_this_estimate: false, linked_line_items_here: [], total_links: 0 } };
      setSavedMeasurements((xs) => [enriched, ...xs]);
      setSelectedIds((s) => { const n = new Set(s); n.add(data.id); return n; });
      toast.success("Solar measurement saved");
      if (onSaved) onSaved(enriched);
    } catch { toast.error("Failed to save Solar measurement"); }
    finally { setSaving(false); }
  };

  if (loadErr) {
    return (
      <div className="p-6 text-sm text-rose-700" data-testid="google-workspace-error">
        Failed to load Google Maps ({loadErr}). Falling back to typed measurement.
      </div>
    );
  }

  const primaryLabel = selectedCount === 1 ? "Add to Estimate" : "Add Selected to Estimate";

  return (
    <div className="grid grid-cols-12 gap-3 h-full" data-testid="google-workspace">
      {/* Left toolbar + measurements panel */}
      <div className="col-span-12 md:col-span-5 lg:col-span-4 xl:col-span-3 flex flex-col h-full overflow-hidden">
        <div className="space-y-3 overflow-y-auto pr-1 flex-1 pb-2">
          <div className="bg-white border border-zinc-200 rounded-lg p-3">
            <Label className="text-xs">Search address</Label>
            <Input
              ref={searchInputRef}
              placeholder="123 Main St, Denver CO"
              defaultValue={propertyAddress || ""}
              data-testid="workspace-search-input"
            />
          </div>

          <div className="bg-white border border-zinc-200 rounded-lg p-3 space-y-2">
            <div className="text-xs font-heading font-bold">Draw</div>
            <div className="grid grid-cols-3 gap-1">
              <Button size="sm" variant={tool === "area" ? "default" : "outline"} onClick={() => setTool("area")} data-testid="tool-area">
                <Square className="w-3 h-3 mr-1" /> Area
              </Button>
              <Button size="sm" variant={tool === "linear" ? "default" : "outline"} onClick={() => setTool("linear")} data-testid="tool-linear">
                <Slash className="w-3 h-3 mr-1" /> Line
              </Button>
              <Button size="sm" variant={tool === "perimeter" ? "default" : "outline"} onClick={() => setTool("perimeter")} data-testid="tool-perimeter">
                <Ruler className="w-3 h-3 mr-1" /> Perim
              </Button>
            </div>
            <div className="flex gap-1">
              <Button size="sm" onClick={finishDraft} disabled={!activeDraft || saving} data-testid="finish-draft-btn">
                Finish
              </Button>
              <Button size="sm" variant="outline" onClick={cancelDraft} disabled={!activeDraft} data-testid="cancel-draft-btn">
                Cancel
              </Button>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={undo} disabled={undoStack.length === 0} data-testid="undo-btn"><Undo2 className="w-3 h-3" /></Button>
              <Button size="sm" variant="outline" onClick={redo} disabled={redoStack.length === 0} data-testid="redo-btn"><Redo2 className="w-3 h-3" /></Button>
              <Button size="sm" variant="outline" onClick={clearAll} disabled={drawings.length === 0} data-testid="clear-btn"><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>

          <div className="bg-white border border-zinc-200 rounded-lg p-3 space-y-2">
            <div className="text-xs font-heading font-bold">View</div>
            <div className="flex gap-1">
              <Button size="sm" variant={mapType === "satellite" ? "default" : "outline"} onClick={() => setMapType("satellite")} data-testid="maptype-satellite">
                <Layers className="w-3 h-3 mr-1" /> Satellite
              </Button>
              <Button size="sm" variant={mapType === "hybrid" ? "default" : "outline"} onClick={() => setMapType("hybrid")} data-testid="maptype-hybrid">
                <Layers className="w-3 h-3 mr-1" /> Hybrid
              </Button>
            </div>
            <Button size="sm" variant="outline" onClick={() => setShowStreet((v) => !v)} data-testid="street-view-btn">
              <Eye className="w-3 h-3 mr-1" /> Street View
            </Button>
          </div>

          {/* Saved measurements cards */}
          <div className="bg-white border border-zinc-200 rounded-lg p-3 space-y-2" data-testid="workspace-measurement-list">
            <div className="text-xs font-heading font-bold flex items-center justify-between">
              <span>Measurements ({savedMeasurements.length})</span>
              {activeDraft && <span className="text-[10px] font-normal text-zinc-500">Drawing…</span>}
            </div>
            {savedMeasurements.length === 0 && !activeDraft && (
              <div className="text-xs text-zinc-500" data-testid="workspace-empty-hint">
                Click the map to start drawing. Finished measurements save automatically.
              </div>
            )}
            {savedMeasurements.map((m) => (
              <MeasurementCard
                key={m.id}
                measurement={m}
                inEstimateContext={inEstimateContext}
                checked={selectedIds.has(m.id)}
                onToggle={() => toggleSelect(m.id)}
                onDelete={() => deleteMeasurement(m)}
              />
            ))}
          </div>

          {solarEnabled && (
            <SolarReviewCard
              lat={propertyLat ?? center?.lat}
              lng={propertyLng ?? center?.lng}
              onConfirm={onSolarConfirm}
              disabled={saving}
            />
          )}
        </div>

        {/* Iter57.4 — sticky bottom action / standalone hint. Sits inside the
            same flex column as the scroll area so it never overlaps saved
            cards or the map canvas; on mobile, the map stacks below this
            column so the CTA is naturally reachable. */}
        <div
          className="sticky bottom-0 z-10 -mx-1 mt-2 pt-2 pb-1 px-1 bg-gradient-to-t from-white via-white to-white/90 border-t border-zinc-200"
          data-testid="workspace-sticky-footer"
        >
          {inEstimateContext ? (
            <Button
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-heading text-base shadow-sm transition-all disabled:opacity-60"
              disabled={!canLink || linking}
              onClick={performLink}
              data-testid="workspace-add-to-estimate-btn"
              aria-busy={linking || undefined}
            >
              {linking ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Adding…
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5 mr-2" />
                  {selectedCount === 0
                    ? "Add to Estimate"
                    : selectedCount === 1
                      ? "Add to Estimate"
                      : `Add Selected to Estimate (${selectedCount})`}
                </>
              )}
            </Button>
          ) : (
            <div
              className="text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 rounded p-3"
              data-testid="workspace-standalone-hint"
            >
              Finished measurements are saved to the property. Open an estimate to add them.
            </div>
          )}
        </div>
      </div>

      {/* Map canvas */}
      <div className="col-span-12 md:col-span-7 lg:col-span-8 xl:col-span-9 relative min-h-[320px]">
        <div ref={mapDivRef} className="absolute inset-0 rounded-lg overflow-hidden" data-testid="google-map-canvas" />
        {showStreet && (
          <div ref={streetDivRef} className="absolute right-3 top-3 w-1/3 h-1/3 border-2 border-white rounded shadow-lg" data-testid="street-view-pane" />
        )}
        <div className="absolute bottom-2 left-2 text-[10px] text-white bg-black/50 px-2 py-1 rounded pointer-events-none">
          Measurements powered by Google Maps
        </div>
      </div>

      {/* Category picker for Finish when no default category */}
      {categoryDialog && (
        <CategoryPickerDialog
          draftMode={categoryDialog.draft?.mode}
          onCancel={() => setCategoryDialog(null)}
          onConfirm={({ category, customLabel }) => {
            const draft = categoryDialog.draft;
            setCategoryDialog(null);
            _saveDraft(draft, {
              service_category: category,
              notes: category === "other" ? customLabel : null,
            });
          }}
        />
      )}

      {/* Multi-match / zero-match target line picker */}
      {linkPickerDialog && (
        <LineTargetPickerDialog
          measurement={linkPickerDialog.measurement}
          candidates={linkPickerDialog.candidates}
          matchedBy={linkPickerDialog.matched_by}
          lineItems={lineItems}
          onCancel={() => setLinkPickerDialog(null)}
          onPick={async (idx) => {
            const m = linkPickerDialog.measurement;
            setLinkPickerDialog(null);
            try {
              await linkOne(m, idx);
              toast.success("Added to estimate");
              // Continue linking remaining selected
              const remaining = Array.from(selectedIds).filter((id) => id !== m.id);
              if (remaining.length > 0) {
                setTimeout(() => performLink(), 0);
              }
            } catch (e) {
              toast.error(`Link failed: ${e?.response?.data?.detail || e?.message || ""}`);
            }
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function MeasurementCard({ measurement: m, inEstimateContext, checked, onToggle, onDelete }) {
  const linked = m?.link_status?.linked_to_this_estimate;
  const status = linked ? "added" : (checked && inEstimateContext ? "ready" : "saved");
  const value = m.square_feet != null ? `${Math.round(m.square_feet).toLocaleString()} ft²`
    : m.linear_feet != null ? `${Math.round(m.linear_feet).toLocaleString()} ft`
    : m.metric_value != null ? `${Math.round(m.metric_value)} m`
    : "—";
  const label = m.notes || categoryLabel(m.service_category) || "Measurement";
  const pill = STATUS_PILLS[status];
  const disableSelect = linked; // linked measurements are not selectable for re-add
  return (
    <div
      className={`border rounded-lg p-2.5 text-xs flex items-start gap-2.5 transition-colors ${
        linked
          ? "bg-emerald-50/60 border-emerald-200"
          : (checked ? "bg-orange-50/60 border-[#FF5A00] ring-1 ring-[#FF5A00]/30" : "border-zinc-200 hover:border-zinc-300")
      }`}
      data-testid={`measurement-card-${m.id}`}
      data-card-status={status}
    >
      {inEstimateContext && (
        <div className="pt-1">
          <Checkbox
            checked={checked}
            onCheckedChange={() => !disableSelect && onToggle()}
            disabled={disableSelect}
            data-testid={`measurement-card-checkbox-${m.id}`}
          />
        </div>
      )}
      <div
        className={`w-8 h-8 shrink-0 rounded-md flex items-center justify-center ${
          linked ? "bg-emerald-100 text-emerald-700"
            : (checked ? "bg-[#FF5A00]/10 text-[#FF5A00]" : "bg-zinc-100 text-zinc-600")
        }`}
        data-testid={`measurement-card-icon-${m.id}`}
      >
        <CategoryIcon category={m.service_category} className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate leading-tight">{label}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-zinc-600 font-mono-accent">
          <span className="uppercase tracking-wider text-[9px] text-zinc-500">{categoryLabel(m.service_category) || m.measurement_type}</span>
          <span className="text-zinc-300">·</span>
          <span>{m.measurement_type}</span>
          <span className="text-zinc-300">·</span>
          <span className="text-zinc-900 font-medium">{value}</span>
        </div>
        <div
          className={`inline-flex items-center mt-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${pill.cls}`}
          data-testid={`measurement-card-status-${m.id}`}
        >
          {pill.label}
        </div>
      </div>
      <button
        onClick={onDelete}
        className="text-zinc-400 hover:text-red-600 p-1 -m-1 rounded transition-colors"
        data-testid={`measurement-card-delete-${m.id}`}
        title="Delete measurement"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

const STATUS_PILLS = {
  saved: { label: "Saved",             cls: "bg-zinc-100 text-zinc-700 border border-zinc-200" },
  ready: { label: "Ready to add",      cls: "bg-orange-100 text-orange-800 border border-orange-200" },
  added: { label: "Added to estimate", cls: "bg-emerald-100 text-emerald-800 border border-emerald-200" },
};

function CategoryPickerDialog({ draftMode, onCancel, onConfirm }) {
  const [cat, setCat] = useState("");
  const [customLabel, setCustomLabel] = useState("");
  const isOther = cat === "other";
  const canConfirm = !!cat && (!isOther || customLabel.trim().length > 0);
  const filtered = MEASUREMENT_CATEGORIES.filter((c) => c.geom === "any" || (draftMode === "linear" ? c.geom === "linear" : c.geom !== "linear"));

  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md" data-testid="category-picker-dialog">
        <DialogHeader>
          <DialogTitle>What is this measuring?</DialogTitle>
          <DialogDescription>Pick a category so we can auto-link this measurement to the right estimate line later.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Category</Label>
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger data-testid="category-picker-select"><SelectValue placeholder="Choose a category" /></SelectTrigger>
              <SelectContent>
                {filtered.map((c) => (
                  <SelectItem key={c.key} value={c.key} data-testid={`category-option-${c.key}`}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isOther && (
            <div>
              <Label className="text-xs">Describe what this is</Label>
              <Input
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                placeholder="e.g. pool coping, fire pit apron"
                data-testid="category-picker-custom-label"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="category-picker-cancel">Cancel</Button>
          <Button
            disabled={!canConfirm}
            onClick={() => onConfirm({ category: cat, customLabel: customLabel.trim() })}
            data-testid="category-picker-confirm"
            className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
          >Save measurement</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LineTargetPickerDialog({ measurement, candidates, matchedBy, lineItems, onCancel, onPick }) {
  const zero = !candidates || candidates.length === 0;
  const showAll = matchedBy === "no_category" || matchedBy === "none";
  const list = zero ? lineItems.map((line, index) => ({ index, line })) : candidates;

  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md" data-testid="link-target-picker-dialog">
        <DialogHeader>
          <DialogTitle>Which line should this go on?</DialogTitle>
          <DialogDescription>
            {zero
              ? "This estimate has no lines yet — add a service line first, or pick an existing one below."
              : (showAll
                  ? "We couldn't match this measurement to a specific service. Pick any line."
                  : "This measurement matches more than one line. Pick which one.")}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-72 overflow-y-auto space-y-1" data-testid="link-target-picker-list">
          {list.length === 0 && (
            <div className="text-xs text-zinc-500 py-3 text-center" data-testid="link-target-picker-empty">
              This estimate has no lines. Add a service line first, then open the workspace again to link this measurement.
            </div>
          )}
          {list.map((x) => (
            <button
              key={x.index}
              onClick={() => onPick(x.index)}
              className="w-full text-left border border-zinc-200 rounded p-2 hover:border-[#FF5A00] hover:bg-orange-50 flex items-center gap-2"
              data-testid={`link-target-picker-option-${x.index}`}
            >
              <MapPin className="w-3 h-3 text-[#FF5A00]" />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{x.line?.name || `Line #${x.index + 1}`}</div>
                <div className="text-[10px] text-zinc-500 font-mono-accent truncate">
                  {(x.line?.service_category || x.line?.service_key || "custom")} · qty {Number(x.line?.quantity || 0).toLocaleString()}
                </div>
              </div>
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="link-target-picker-cancel">Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function _ringFromPath(lst) {
  const ring = lst.map((p) => [p.lng, p.lat]);
  if (ring.length > 0 && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) {
    ring.push([ring[0][0], ring[0][1]]);
  }
  return ring;
}

function _pointBbox(lat, lng, meters) {
  const dLat = meters / 111_320;
  const dLng = meters / (111_320 * Math.cos((lat * Math.PI) / 180));
  return [
    [lng - dLng, lat - dLat],
    [lng - dLng, lat + dLat],
    [lng + dLng, lat + dLat],
    [lng + dLng, lat - dLat],
    [lng - dLng, lat - dLat],
  ];
}
